/**
 * AfterHoursToggle: the Regular | After Hours choice, one segmented
 * toggle wherever a block of time can be after hours (the note composer,
 * the office's Pre-billing row editor).
 *
 * One radiogroup named "Regular or After Hours" with two radios, Regular
 * then After Hours, drawn by SegmentedToggle: a Button with role="radio",
 * aria-checked and a roving tabIndex each, arrow keys that check and
 * focus the next or previous radio, wrapping. A host with a visible label
 * names the group with `labelledBy`: the group then carries
 * aria-labelledby beside its aria-label, and its name is the visible
 * label.
 *
 * The colours: Regular checked is forest, the primary Button. After Hours
 * checked is the house gold of the Recommended chip (warm-400) with its
 * forest-700 text. The same on every face; the toggle takes no accent.
 *
 * Ids: `${testIdPrefix}` is the radiogroup, `${testIdPrefix}-regular` and
 * `${testIdPrefix}-after` the radios. A host that does not offer after
 * hours (the admin band) does not render this at all.
 */

import { type ReactNode } from 'react';
import { SegmentedToggle, type SegmentedOption } from '@/components/primitives/SegmentedToggle';

export interface AfterHoursToggleProps {
  /** true = After Hours. */
  value: boolean;
  onChange: (next: boolean) => void;
  testIdPrefix: string;
  /** The id of the host's visible label; the group's accessible name. */
  labelledBy?: string;
  /** Both radios shown, faded and unchoosable (a note whose pay period
   *  has closed). */
  disabled?: boolean;
}

const OPTIONS: readonly [SegmentedOption<boolean>, SegmentedOption<boolean>] = [
  { value: false, label: 'Regular', suffix: 'regular' },
  { value: true, label: 'After Hours', suffix: 'after', checkedLook: 'gold' },
];

const DISABLED_OPTIONS: readonly [SegmentedOption<boolean>, SegmentedOption<boolean>] = [
  { ...OPTIONS[0], disabled: true },
  { ...OPTIONS[1], disabled: true },
];

export function AfterHoursToggle({ value, onChange, testIdPrefix, labelledBy, disabled = false }: AfterHoursToggleProps): ReactNode {
  return (
    <SegmentedToggle
      options={disabled ? DISABLED_OPTIONS : OPTIONS}
      value={value}
      onChange={onChange}
      name="Regular or After Hours"
      labelledBy={labelledBy}
      testIdPrefix={testIdPrefix}
    />
  );
}
